import { useMutation, useQueryClient } from '@tanstack/react-query';
import { assetUrl, writeJson } from '../addOnAssets';
import { quizKey } from './useQuiz';
import type { QuizBank, QuizQuestion, QuizSaver, UseQuizArgs } from './types';
import type { AssetPatchBody } from '../../types';

/**
 * Writes a project's Quiz questions back to its asset:
 * `PATCH /media/assets/{assetId}` with `{ add_on: { questions } }`.
 *
 * The bank is written into the query cache when the save STARTS, not when it
 * succeeds, and the cache is not invalidated. Invalidating refetches every
 * mounted `useQuiz`, and that read is a three-request chain (project → asset →
 * the asset's signed path) whose result an open editor cannot even use — it
 * seeds its rows once, at mount. This is a whole-asset replace, so what was
 * sent is what is stored: the cache is already correct, and a card reopened
 * later seeds from it without a request.
 *
 * Seeding at the start is what makes a card reopened MID-FLIGHT correct. These
 * cards unmount on every rail switch and flush their debounced save on the way
 * out, so a PATCH is routinely open with no card mounted. Seeding on success
 * instead would leave the cache pre-edit for that whole flight: switching back
 * before it lands would seed the rows from stale data, and the next save would
 * ship the pre-edit bank back over the edit that was in flight.
 *
 * On failure the cache goes back to what it held before the write. Not a
 * re-read: a PATCH that failed for want of network would take the re-read
 * down with it, and a read error unmounts the editor — with everything typed.
 */
export function useSaveQuiz({
  apiBaseUrl,
  projectId,
  token,
}: UseQuizArgs): QuizSaver {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      assetId,
      questions,
    }: {
      assetId: string;
      questions: QuizQuestion[];
    }) =>
      writeJson(
        assetUrl(apiBaseUrl, assetId),
        { add_on: { questions } } satisfies AssetPatchBody,
        token,
      ),
    onMutate: async ({ assetId, questions }) => {
      const queryKey = quizKey(apiBaseUrl, projectId);
      // A read still in flight would land after this write and put the
      // pre-edit bank back.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<QuizBank>(queryKey);
      queryClient.setQueryData<QuizBank>(queryKey, { questions, assetId });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      const queryKey = quizKey(apiBaseUrl, projectId);
      // setQueryData ignores `undefined`, so an empty cache is emptied again
      // rather than left holding the rejected bank.
      if (context?.previous) {
        queryClient.setQueryData<QuizBank>(queryKey, context.previous);
      } else {
        queryClient.removeQueries({ queryKey });
      }
    },
  });

  return {
    save: (assetId, questions) => mutation.mutateAsync({ assetId, questions }),
    isSaving: mutation.isLoading,
  };
}
